import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Trash2, LoaderCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { apiError } from '../../utils/apiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GameRemovalConfirmation = ({ game, onCancel, onRemoved, onBusyChange }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const locked = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const confirm = async () => {
    if (locked.current || !game.isOwner || game.removalBlocked) return;
    locked.current = true; setBusy(true); onBusyChange(true); setError('');
    try {
      await axios.delete(`${API}${game.deletePath}`, { timeout: 20000 });
      if (mounted.current) onRemoved(game);
    } catch (err) {
      if (!mounted.current) return;
      if (err.response?.status === 404) onRemoved(game);
      else setError(err.code === 'ECONNABORTED' ? 'Removal timed out. Refresh your games before trying again.' : apiError(err, 'Removal could not be confirmed. Refresh your games before trying again.'));
    } finally {
      if (mounted.current) { setBusy(false); onBusyChange(false); }
      locked.current = false;
    }
  };
  return <div className="space-y-4" data-testid="game-removal-confirmation">
    <p className="font-semibold break-words [overflow-wrap:anywhere]" data-testid="game-removal-name">{game.name}</p>
    <p className="text-sm text-[var(--ps-text-secondary)]" data-testid="game-removal-warning">{game.type === 'solo'
      ? 'Discard this saved draft and its unfinished scores? This cannot be undone.'
      : 'Delete this game and its game scorecards for all participants? This cannot be undone. Rounds already saved to personal history will remain.'}</p>
    {error && <p role="alert" className="text-sm text-red-600 break-words" data-testid="game-removal-error">{error}</p>}
    <div className="flex flex-wrap justify-end gap-2">
      <Button type="button" variant="outline" autoFocus disabled={busy} data-testid="game-removal-cancel" onClick={onCancel}>Keep game</Button>
      <Button type="button" variant="destructive" disabled={busy} data-testid="game-removal-confirm" onClick={confirm}>
        {busy ? <LoaderCircle size={16} className="animate-spin" /> : <Trash2 size={16} />}{busy ? 'Removing…' : game.removalLabel}
      </Button>
    </div>
  </div>;
};